import React from "react";
import { format, addDays } from "date-fns";
import { Controller, useFormContext } from "react-hook-form";
import { CalendarIcon, Minus, Clock } from "lucide-react";
import { ProgramCategory, DEFAULT_PROGRAM_DURATIONS } from "@/services/db";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface ProgramFormCardProps {
  index: number;
  programCategories: ProgramCategory[];
  programsByCategory: Record<ProgramCategory, string[]>;
  canDelete: boolean;
  onRemove: () => void;
  handleCategoryChange: (value: string, index: number) => void;
  handleProgramChange: (value: string, index: number) => void;
  calculateCompletionDate: (enrollmentDate: Date, duration: number) => Date;
}

const ProgramFormCard: React.FC<ProgramFormCardProps> = ({
  index,
  programCategories,
  programsByCategory,
  canDelete,
  onRemove,
  handleCategoryChange,
  handleProgramChange,
  calculateCompletionDate,
}) => {
  const form = useFormContext();

  const enrollmentDate = form.watch(`programs.${index}.enrolledAt`);
  const duration = form.watch(`programs.${index}.duration`);

  React.useEffect(() => {
    if (enrollmentDate && duration) {
      const completionDate = calculateCompletionDate(enrollmentDate, Number(duration));
      form.setValue(`programs.${index}.completionDate`, completionDate);
    }
  }, [enrollmentDate, duration, calculateCompletionDate, form, index]);

  return (
    <Card key={index} className="bg-slate-50 dark:bg-nalc-dark/20">
      <CardContent className="pt-4 space-y-4">
        <div className="flex items-center justify-between pb-2 border-b mb-2">
          <h4 className="font-medium">Program #{index + 1}</h4>
          {canDelete && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onRemove}
              className="text-red-500 h-8 w-8 p-0"
              aria-label="Remove program"
            >
              <Minus className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor={`programs.${index}.category`}>Program Category</Label>
          <Controller
            control={form.control}
            name={`programs.${index}.category`}
            render={({ field }) => (
              <Select
                onValueChange={(value) => handleCategoryChange(value, index)}
                value={field.value}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {programCategories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {form.formState.errors.programs?.[index]?.category && (
            <p className="text-sm text-red-500">
              {form.formState.errors.programs[index]?.category?.message}
            </p>
          )}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor={`programs.${index}.title`}>Program Title</Label>
          <Controller
            control={form.control}
            name={`programs.${index}.title`}
            render={({ field }) => (
              <Select
                onValueChange={(value) => handleProgramChange(value, index)}
                value={field.value}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a program" />
                </SelectTrigger>
                <SelectContent>
                  {programsByCategory[form.watch(`programs.${index}.category`) as ProgramCategory]?.map((program) => (
                    <SelectItem key={program} value={program}>
                      {program}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {form.formState.errors.programs?.[index]?.title && (
            <p className="text-sm text-red-500">
              {form.formState.errors.programs[index]?.title?.message}
            </p>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor={`programs.${index}.fee`}>Program Fee ($)</Label>
            <Input
              id={`programs.${index}.fee`}
              type="number"
              min="0"
              step="0.01"
              {...form.register(`programs.${index}.fee`)}
              placeholder="0.00"
            />
            {form.formState.errors.programs?.[index]?.fee && (
              <p className="text-sm text-red-500">
                {form.formState.errors.programs[index]?.fee?.message}
              </p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor={`programs.${index}.duration`}>Duration (Days)</Label>
            <div className="flex items-center">
              <Input
                id={`programs.${index}.duration`}
                type="number"
                min="1"
                {...form.register(`programs.${index}.duration`, {
                  onChange: (e) => {
                    const durationValue = Number(e.target.value);
                    const enrollmentDate = form.watch(`programs.${index}.enrolledAt`);
                    if (enrollmentDate && durationValue > 0) {
                      form.setValue(`programs.${index}.completionDate`, 
                        calculateCompletionDate(enrollmentDate, durationValue));
                    }
                  }
                })}
                placeholder="30"
              />
              <Clock className="ml-2 h-4 w-4 text-muted-foreground" />
            </div>
            {form.formState.errors.programs?.[index]?.duration && (
              <p className="text-sm text-red-500">
                {form.formState.errors.programs[index]?.duration?.message}
              </p>
            )}
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor={`programs.${index}.enrolledAt`}>Enrollment Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                id={`programs.${index}.enrolledAt`}
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !form.watch(`programs.${index}.enrolledAt`) && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {form.watch(`programs.${index}.enrolledAt`) ? (
                  format(form.watch(`programs.${index}.enrolledAt`), "PPP")
                ) : (
                  <span>Select a date</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={form.watch(`programs.${index}.enrolledAt`)}
                onSelect={(date) => {
                  if (date) {
                    form.setValue(`programs.${index}.enrolledAt`, date);
                    
                    const duration = form.watch(`programs.${index}.duration`);
                    if (duration) {
                      form.setValue(`programs.${index}.completionDate`, 
                        calculateCompletionDate(date, Number(duration)));
                    }
                  }
                }}
                disabled={(date) => date > new Date()}
                initialFocus
                className="pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
          {form.formState.errors.programs?.[index]?.enrolledAt && (
            <p className="text-sm text-red-500">
              {form.formState.errors.programs[index]?.enrolledAt?.message}
            </p>
          )}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor={`programs.${index}.feeStatus`}>Fee Status</Label>
          <Controller
            control={form.control}
            name={`programs.${index}.feeStatus`}
            render={({ field }) => (
              <Select
                onValueChange={field.onChange}
                value={field.value}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select fee status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          {form.formState.errors.programs?.[index]?.feeStatus && (
            <p className="text-sm text-red-500">
              {form.formState.errors.programs[index]?.feeStatus?.message}
            </p>
          )}
        </div>
        
        {form.watch(`programs.${index}.feeStatus`) !== 'paid' && (
          <div className="space-y-2">
            <Label htmlFor={`programs.${index}.nextPaymentDate`}>Next Payment Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  id={`programs.${index}.nextPaymentDate`}
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !form.watch(`programs.${index}.nextPaymentDate`) && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {form.watch(`programs.${index}.nextPaymentDate`) ? (
                    format(form.watch(`programs.${index}.nextPaymentDate`), "PPP")
                  ) : (
                    <span>Select a date</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={form.watch(`programs.${index}.nextPaymentDate`)}
                  onSelect={(date) => form.setValue(`programs.${index}.nextPaymentDate`, date)}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>
        )}
        
        <div className="mt-4 bg-nalc-teal/10 p-3 rounded-md">
          <div className="flex items-start space-x-2">
            <Clock className="h-5 w-5 text-nalc-teal mt-0.5" />
            <div>
              <h5 className="font-medium text-sm">Program Completion</h5>
              <p className="text-sm">
                {form.watch(`programs.${index}.enrolledAt`) && form.watch(`programs.${index}.duration`) ? (
                  format(
                    calculateCompletionDate(
                      form.watch(`programs.${index}.enrolledAt`),
                      Number(form.watch(`programs.${index}.duration`))
                    ),
                    "PPP"
                  )
                ) : "Not set"}
              </p>
              <input 
                type="hidden" 
                {...form.register(`programs.${index}.completionDate`)}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProgramFormCard;
